
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';

export default function PhotovoltaikPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBack = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('leistungen');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleContactClick = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('kontakt');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        window.dispatchEvent(new CustomEvent('preselect-service', { detail: 'Photovoltaik (PV-Anlage)' }));
      }
    }, 100);
  };

  const features = [
    { icon: 'ri-money-euro-circle-line', title: 'Kostenersparnis', description: 'Senken Sie Ihre Stromkosten nachhaltig und profitieren Sie von staatlichen Förderungen' },
    { icon: 'ri-leaf-line', title: 'Umweltschutz', description: 'Produzieren Sie saubere Energie und leisten Sie einen aktiven Beitrag zum Klimaschutz' },
    { icon: 'ri-line-chart-line', title: 'Maximale Erträge', description: 'Modernste Technologie und hochwertige Komponenten für optimale Leistung' },
    { icon: 'ri-shield-check-line', title: 'Rundum-Service', description: 'Von der Planung über Installation bis zur Wartung – alles aus einer Hand' },
  ];

  const benefits = [
    'Professionelle Planung und Beratung',
    'Hochwertige Komponenten und Module',
    'Fachgerechte Installation durch Experten',
    'Maximale Energieausbeute',
    'Langfristige Wartung und Support',
    'Staatliche Förderungen nutzen',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Header */}
      <div className="relative bg-gradient-to-br from-amber-400 via-yellow-500 to-orange-500 pt-24 sm:pt-32 pb-14 sm:pb-20 px-4 sm:px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/20"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <button
            onClick={handleBack}
            className="inline-flex items-center text-white/90 hover:text-white transition-colors cursor-pointer bg-transparent border-none p-0 mb-6 sm:mb-8 text-sm sm:text-base"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Zurück zu allen Leistungen
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-2xl shrink-0">
              <i className="ri-sun-line text-4xl sm:text-5xl text-white"></i>
            </div>
            <div>
              <h1 className="text-3xl sm:text-5xl font-bold text-white mb-1 sm:mb-2">Photovoltaik</h1>
              <p className="text-base sm:text-xl text-white/90">Ihre eigene Stromproduktion mit Sonnenenergie</p>
            </div>
          </div>
        </div>
      </div>

      <main className="pb-16 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">

          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 -mt-8 sm:-mt-10 relative z-20 mb-10 sm:mb-12">
            <p className="text-gray-700 leading-relaxed text-base sm:text-lg mb-4 sm:mb-6">
              Nutzen Sie die Kraft der Sonne und machen Sie sich unabhängig von steigenden Strompreisen.
              Unsere maßgeschneiderten Photovoltaik-Anlagen verwandeln Sonnenlicht in saubere Energie für Ihr Zuhause oder Unternehmen.
            </p>
            <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
              Von der professionellen Planung über die fachgerechte Installation bis hin zur Wartung – wir begleiten Sie auf dem Weg zur eigenen Stromproduktion.
              Mit modernster Technologie, hochwertigen Komponenten und jahrelanger Erfahrung sorgen wir dafür, dass Ihre PV-Anlage maximale Erträge liefert.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-10 sm:mb-12">
            {features.map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 sm:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg mb-3 sm:mb-4">
                  <i className={`${feature.icon} text-xl sm:text-2xl text-white`}></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-[#1A2B4A] mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-[#1A2B4A] to-[#2A3B5A] rounded-2xl p-6 sm:p-8 mb-10 sm:mb-12 text-white">
            <h2 className="text-xl sm:text-2xl font-bold mb-5 sm:mb-6">Unsere Leistungen im Überblick</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#F4B942] text-xl mr-3 mt-0.5 shrink-0"></i>
                  <span className="text-white/90 text-sm sm:text-base">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 sm:p-10 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A2B4A] mb-3 sm:mb-4">
              Bereit für Ihre eigene PV-Anlage?
            </h2>
            <p className="text-gray-700 text-base sm:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto">
              Lassen Sie sich kostenlos beraten und erfahren Sie, wie viel Sie mit einer Photovoltaik-Anlage sparen können.
            </p>
            <button
              onClick={handleContactClick}
              className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-semibold rounded-lg hover:shadow-xl transition-all whitespace-nowrap cursor-pointer border-none text-base"
            >
              Jetzt Anfrage senden
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
